(() => {
  const API_PROMISE_KEY = '__fyreYouTubeApiPromise';

  const loadYouTubeApi = () => {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (window[API_PROMISE_KEY]) return window[API_PROMISE_KEY];

    window[API_PROMISE_KEY] = new Promise((resolve, reject) => {
      const previousReady = window.onYouTubeIframeAPIReady;
      let attempts = 0;

      const finish = () => {
        if (!window.YT?.Player) return false;
        resolve(window.YT);
        return true;
      };

      window.onYouTubeIframeAPIReady = () => {
        if (typeof previousReady === 'function') previousReady();
        finish();
      };

      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        script.onerror = () => reject(new Error('The YouTube player API could not be loaded.'));
        document.head.appendChild(script);
      }

      const poll = window.setInterval(() => {
        attempts += 1;
        if (finish() || attempts >= 200) {
          window.clearInterval(poll);
          if (attempts >= 200 && !window.YT?.Player) {
            reject(new Error('The YouTube player API timed out.'));
          }
        }
      }, 50);
    });

    return window[API_PROMISE_KEY];
  };

  class FyreYouTubeGallery extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;

      const customId = this.dataset.customId;
      if (customId) this.setAttribute('data-gallery-custom-id', customId);

      this.iframe = this.querySelector('[data-youtube-gallery-player]');
      this.playerShell = this.querySelector('.fyre-youtube-gallery__player-shell');
      this.browser = this.querySelector('[data-youtube-gallery-browser]');
      this.thumbnailRail = this.querySelector('[data-youtube-gallery-thumbnails]');
      this.indicatorRail = this.querySelector('[data-youtube-gallery-indicators]');
      this.perspectiveNavigation = this.querySelector('[data-youtube-gallery-perspective]');
      this.previousPreview = this.querySelector('[data-youtube-gallery-preview-previous]');
      this.nextPreview = this.querySelector('[data-youtube-gallery-preview-next]');
      this.perspectiveArrows = this.querySelectorAll('[data-youtube-gallery-perspective-arrow]');
      this.backdrop = this.querySelector('[data-youtube-gallery-backdrop]');
      this.counter = this.querySelector('[data-youtube-gallery-counter]');
      this.currentCounter = this.querySelector('[data-youtube-gallery-current]');
      this.totalCounter = this.querySelector('[data-youtube-gallery-total]');
      this.currentCounters = this.querySelectorAll('[data-youtube-gallery-current]');
      this.totalCounters = this.querySelectorAll('[data-youtube-gallery-total]');

      if (!this.iframe || (!this.dataset.playlistId && !this.dataset.videoId)) return;

      this.handleClick = this.handleClick.bind(this);
      this.addEventListener('click', this.handleClick);

      loadYouTubeApi()
        .then((YT) => {
          if (!this.isConnected) return;
          this.player = new YT.Player(this.iframe, {
            events: {
              onReady: () => this.findPlaylist(),
              onStateChange: () => this.updateActiveThumbnail(),
            },
          });
        })
        .catch(() => {
          // The native playlist iframe remains fully usable if the optional API fails.
        });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      if (this.playlistTimer) window.clearTimeout(this.playlistTimer);
      if (this.transitionTimer) window.clearTimeout(this.transitionTimer);
      if (this.settleTimer) window.clearTimeout(this.settleTimer);
      this.transitionPreview?.remove();
      if (this.player?.destroy) this.player.destroy();
      if (this.ambientRequest) {
        this.ambientRequest.onload = null;
        this.ambientRequest.onerror = null;
      }
      this.player = null;
      this.ambientRequest = null;
      this.isInitialized = false;
    }

    findPlaylist(attempt = 0) {
      if (!this.player?.getPlaylist) return;

      const playlist = this.player.getPlaylist() || [];
      if (playlist.length) {
        this.videoIds = playlist;
        this.renderThumbnails();
        this.renderIndicators();

        const requestedIndex = this.dataset.videoId ? playlist.indexOf(this.dataset.videoId) : -1;
        const currentIndex = this.player.getPlaylistIndex?.() ?? -1;

        if (requestedIndex >= 0 && requestedIndex !== currentIndex) {
          if (this.dataset.autoplay === 'true' && this.player.loadPlaylist) {
            this.player.loadPlaylist(playlist, requestedIndex, 0);
          } else if (this.player.cuePlaylist) {
            this.player.cuePlaylist(playlist, requestedIndex, 0);
          }
          this.setActiveThumbnail(requestedIndex);
        } else {
          this.setActiveThumbnail(currentIndex >= 0 ? currentIndex : 0);
        }
        return;
      }

      if (this.dataset.videoId && (!this.dataset.playlistId || attempt >= 12)) {
        this.videoIds = [this.dataset.videoId];
        this.renderThumbnails();
        this.renderIndicators();
        this.setActiveThumbnail(0);
        return;
      }

      if (attempt < 12) {
        this.playlistTimer = window.setTimeout(() => this.findPlaylist(attempt + 1), 250);
      }
    }

    renderThumbnails() {
      if (!this.thumbnailRail || !this.videoIds?.length) return;

      const fragment = document.createDocumentFragment();
      this.videoIds.forEach((videoId, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fyre-youtube-gallery__thumbnail';
        button.dataset.youtubeGalleryIndex = String(index);
        button.setAttribute('role', 'listitem');
        button.setAttribute('aria-label', `Play video ${index + 1}`);
        button.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');

        const image = document.createElement('img');
        image.src = `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/mqdefault.jpg`;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 320;
        image.height = 180;
        button.appendChild(image);
        fragment.appendChild(button);
      });

      this.thumbnailRail.replaceChildren(fragment);
      if (this.browser) this.browser.hidden = this.videoIds.length < 1;
      if (this.counter) this.counter.hidden = this.videoIds.length < 1;
      this.totalCounters.forEach((counter) => {
        counter.textContent = String(this.videoIds.length);
      });
    }

    renderIndicators() {
      if (!this.indicatorRail || !this.videoIds?.length) return;

      const fragment = document.createDocumentFragment();
      this.videoIds.forEach((_videoId, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fyre-youtube-gallery__indicator';
        button.dataset.youtubeGalleryIndex = String(index);
        button.setAttribute('role', 'listitem');
        button.setAttribute('aria-label', `Play video ${index + 1}`);
        button.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');
        fragment.appendChild(button);
      });

      this.indicatorRail.replaceChildren(fragment);
      if (this.perspectiveNavigation) this.perspectiveNavigation.hidden = false;
      this.totalCounters.forEach((counter) => {
        counter.textContent = String(this.videoIds.length);
      });
    }

    handleClick(event) {
      if (!this.player) return;

      const thumbnail = event.target.closest('[data-youtube-gallery-index]');
      if (thumbnail) {
        const index = Number(thumbnail.dataset.youtubeGalleryIndex);
        if (Number.isInteger(index)) {
          const currentIndex = Number.isInteger(this.activeIndex) ? this.activeIndex : 0;
          const direction = index < currentIndex ? 'previous' : 'next';
          this.transitionToVideo(index, direction);
        }
        return;
      }

      if (event.target.closest('[data-youtube-gallery-previous]')) {
        if (!this.videoIds?.length) return;
        const index = (Number.isInteger(this.activeIndex) ? this.activeIndex : 0) - 1;
        const previousIndex = (index + this.videoIds.length) % this.videoIds.length;
        this.transitionToVideo(previousIndex, 'previous');
        return;
      }

      if (event.target.closest('[data-youtube-gallery-next]')) {
        if (!this.videoIds?.length) return;
        const index = (Number.isInteger(this.activeIndex) ? this.activeIndex : 0) + 1;
        const nextIndex = index % this.videoIds.length;
        this.transitionToVideo(nextIndex, 'next');
      }
    }

    transitionToVideo(index, direction) {
      if (!this.player || !this.videoIds?.[index] || this.isTransitioning || index === this.activeIndex) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!this.playerShell || prefersReducedMotion) {
        this.player.playVideoAt(index);
        this.setActiveThumbnail(index, true);
        return;
      }

      const transitionDirection = direction === 'previous' ? 'previous' : 'next';
      const transitionDuration = this.dataset.playerStyle === 'perspective' ? 620 : 440;
      this.isTransitioning = true;
      this.transitionDirection = transitionDirection;

      if (this.dataset.playerStyle === 'perspective') {
        const enteringPreview = transitionDirection === 'previous' ? this.previousPreview : this.nextPreview;
        if (enteringPreview) {
          enteringPreview.src = `https://i.ytimg.com/vi/${encodeURIComponent(this.videoIds[index])}/hqdefault.jpg`;
        }
      } else {
        this.createTransitionPreview(index, transitionDirection);
      }

      this.classList.add('is-transitioning', `is-transitioning-${transitionDirection}`);

      this.transitionTimer = window.setTimeout(() => {
        this.completeVideoTransition(index, transitionDirection);
        this.transitionTimer = null;
      }, transitionDuration);
    }

    createTransitionPreview(index, direction) {
      this.transitionPreview?.remove();

      const preview = document.createElement('div');
      preview.className = `fyre-youtube-gallery__transition-preview fyre-youtube-gallery__transition-preview--${direction}`;
      preview.setAttribute('aria-hidden', 'true');

      const image = document.createElement('img');
      image.src = `https://i.ytimg.com/vi/${encodeURIComponent(this.videoIds[index])}/hqdefault.jpg`;
      image.alt = '';
      preview.appendChild(image);
      this.querySelector('.fyre-youtube-gallery__stage')?.appendChild(preview);
      this.transitionPreview = preview;
    }

    completeVideoTransition(index, direction) {
      if (!this.isConnected || !this.player) return;

      if (this.dataset.playerStyle === 'perspective') {
        this.createTransitionPreview(index, direction);
      }

      this.classList.add('is-resetting');
      this.player.playVideoAt(index);
      this.setActiveThumbnail(index, true);
      this.classList.remove('is-transitioning', `is-transitioning-${direction}`);
      this.isTransitioning = false;

      window.requestAnimationFrame(() => {
        this.classList.remove('is-resetting');
        this.transitionPreview?.classList.add('is-settling');

        this.settleTimer = window.setTimeout(() => {
          this.transitionPreview?.remove();
          this.transitionPreview = null;
          this.settleTimer = null;
        }, 240);
      });
    }

    updateActiveThumbnail(scroll = false) {
      if (!this.player?.getPlaylistIndex) return;
      const index = this.player.getPlaylistIndex();
      if (index >= 0) this.setActiveThumbnail(index, scroll);
    }

    setActiveThumbnail(index, scroll = false) {
      this.activeIndex = index;
      const thumbnails = this.querySelectorAll('[data-youtube-gallery-index]');
      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const active = thumbnailIndex === index;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.currentCounters.forEach((counter) => {
        counter.textContent = String(index + 1);
      });
      this.updateAmbientBackdrop(index);
      this.updatePerspectivePreviews(index);

      if (scroll && this.thumbnailRail) {
        const activeThumbnail = this.thumbnailRail.querySelector(`[data-youtube-gallery-index="${index}"]`);
        if (activeThumbnail) {
          const railRect = this.thumbnailRail.getBoundingClientRect();
          const thumbnailRect = activeThumbnail.getBoundingClientRect();
          const centeredLeft = this.thumbnailRail.scrollLeft
            + thumbnailRect.left
            - railRect.left
            - (railRect.width - thumbnailRect.width) / 2;
          const maximumLeft = this.thumbnailRail.scrollWidth - this.thumbnailRail.clientWidth;

          this.thumbnailRail.scrollTo({
            left: Math.min(Math.max(0, centeredLeft), Math.max(0, maximumLeft)),
            behavior: 'smooth',
          });
        }
      }
    }

    updatePerspectivePreviews(index) {
      if (!this.videoIds?.length || (!this.previousPreview && !this.nextPreview)) return;

      const previousIndex = (index - 1 + this.videoIds.length) % this.videoIds.length;
      const nextIndex = (index + 1) % this.videoIds.length;

      if (this.previousPreview) {
        this.previousPreview.src = `https://i.ytimg.com/vi/${encodeURIComponent(this.videoIds[previousIndex])}/hqdefault.jpg`;
        this.previousPreview.closest('button').hidden = this.videoIds.length < 2;
      }
      if (this.nextPreview) {
        this.nextPreview.src = `https://i.ytimg.com/vi/${encodeURIComponent(this.videoIds[nextIndex])}/hqdefault.jpg`;
        this.nextPreview.closest('button').hidden = this.videoIds.length < 2;
      }
      this.perspectiveArrows.forEach((arrow) => {
        arrow.hidden = this.videoIds.length < 2;
      });
    }

    updateAmbientBackdrop(index) {
      if (this.dataset.backgroundMode !== 'ambient' || !this.backdrop || !this.videoIds?.[index]) return;

      const videoId = this.videoIds[index];
      const imageUrl = `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`;

      // Display the active thumbnail immediately. Color sampling is an enhancement,
      // so the ambient background still works if the image host blocks canvas reads.
      this.backdrop.style.backgroundImage = `url("${imageUrl}")`;
      this.backdrop.classList.add('is-ready');

      if (this.ambientRequest) {
        this.ambientRequest.onload = null;
        this.ambientRequest.onerror = null;
      }

      const image = new Image();
      this.ambientRequest = image;
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = () => {
        if (this.activeIndex !== index) return;
        this.updateAmbientColor(image);
      };
      image.src = imageUrl;
    }

    updateAmbientColor(image) {
      try {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return;

        canvas.width = 24;
        canvas.height = 14;
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        let red = 0;
        let green = 0;
        let blue = 0;
        let samples = 0;

        for (let position = 0; position < pixels.length; position += 16) {
          const brightness = pixels[position] + pixels[position + 1] + pixels[position + 2];
          if (brightness < 24 || brightness > 735) continue;
          red += pixels[position];
          green += pixels[position + 1];
          blue += pixels[position + 2];
          samples += 1;
        }

        if (!samples) return;
        this.style.setProperty('--fyre-youtube-gallery-ambient-rgb', `${Math.round(red / samples)}, ${Math.round(green / samples)}, ${Math.round(blue / samples)}`);
      } catch (_error) {
        // Cross-origin canvas access is optional; the blurred thumbnail remains visible.
      }
    }
  }

  if (!customElements.get('fyre-youtube-gallery')) {
    customElements.define('fyre-youtube-gallery', FyreYouTubeGallery);
  }
})();
